import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../utils/idbUtils";

const DB_NAME = "grpc_satelite";
const DB_VERSION = 4;
const STORE_ESCENAS = "escenas";
const STORE_ESTADISTICAS = "estadisticas";
const STORE_SYNC = "sync";

/** Item mínimo que necesita el merge de delta (mismo contrato que
 *  `GrpcLoteItem`/etc.: watermark por scope -finca o lote-, no uno global). */
export type GrpcSateliteItem = {
  id: string;
  eliminado: boolean;
  ultimaActualizacion: bigint;
};

/** IndexedDB **nueva y separada** para escenas/estadísticas satelitales que
 *  trae `SateliteGrpcService` (ver CLAUDE.md del Server, sección migración a
 *  gRPC-Web) — mismo patrón que `GrpcLoteDatabase`, con dos stores en vez de
 *  uno porque son dos listados independientes (por finca y por lote) que no
 *  comparten índice. El watermark se guarda por `scope` (`escena:{fincaId}` /
 *  `estadistica:{loteId}`) en un único store `sync` compartido.
 *
 *  **Cada upgrade de `DB_VERSION` vacía y recrea todos los stores**
 *  (`vaciarAntesDe: DB_VERSION` en `fx_crearBaseLocal`, mismo criterio que
 *  `GrpcCatalogosDatabase`, 2026-09-15): es la única forma de "restaurar" el
 *  cache local a un esquema/dato
 *  nuevo — DB_VERSION 2 (16-09-2026) existe para descartar los
 *  `cloudsPercentage`/`shadowsPercentage` guardados en `null` por el bug de
 *  `EscenaGrpcService.toEscenaResponse` (ver ese archivo); sin el reset, el
 *  sync delta nunca los vuelve a pedir porque el watermark ya pasó esa fecha. */
class GrpcSateliteDatabase {
  /** Conexión a la base local con el ayudante común. Esta base se vacía en
   *  cada cambio de `DB_VERSION` (ver `fx_crearBaseLocal`). */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_ESCENAS, clave: "id", indices: ["fincaId"] },
      { nombre: STORE_ESTADISTICAS, clave: "id", indices: ["fincaLoteId"] },
      { nombre: STORE_SYNC, clave: "scope" },
    ],
    { vaciarAntesDe: DB_VERSION },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  /** Watermark de la última sincronización exitosa de un scope
   *  (`escena:{fincaId}` / `estadistica:{loteId}`). `0n` si nunca sincronizó. */
  private async getUltimaSincronizacionAsync(scope: string): Promise<bigint> {
    const db = await this.open();
    const store = db.transaction(STORE_SYNC, "readonly").objectStore(STORE_SYNC);
    const row = await reqToPromise<{ scope: string; ultimaSincronizacion: string } | undefined>(
      store.get(scope),
    );
    return row ? BigInt(row.ultimaSincronizacion) : 0n;
  }

  private async putDeltaAsync<T extends GrpcSateliteItem>(
    storeName: string,
    scope: string,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([storeName, STORE_SYNC], "readwrite");
    const store = tx.objectStore(storeName);
    const syncStore = tx.objectStore(STORE_SYNC);

    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    syncStore.put({ scope, ultimaSincronizacion: nuevoWatermark.toString() });

    await txDone(tx);
  }

  // ─── Escenas (por fincaId) ───

  async getUltimaSincronizacionEscenasAsync(fincaId: string): Promise<bigint> {
    return this.getUltimaSincronizacionAsync(`escena:${fincaId}`);
  }

  async putEscenasDeltaAsync<T extends GrpcSateliteItem>(
    fincaId: string,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    await this.putDeltaAsync(STORE_ESCENAS, `escena:${fincaId}`, items, nuevoWatermark);
  }

  async getEscenasByFincaIdAsync<T>(fincaId: string): Promise<T[]> {
    const db = await this.open();
    const index = db
      .transaction(STORE_ESCENAS, "readonly")
      .objectStore(STORE_ESCENAS)
      .index("fincaId");
    return reqToPromise<T[]>(index.getAll(fincaId));
  }

  // ─── Estadísticas (por loteId) ───

  async getUltimaSincronizacionEstadisticasAsync(loteId: string): Promise<bigint> {
    return this.getUltimaSincronizacionAsync(`estadistica:${loteId}`);
  }

  async putEstadisticasDeltaAsync<T extends GrpcSateliteItem>(
    loteId: string,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    await this.putDeltaAsync(STORE_ESTADISTICAS, `estadistica:${loteId}`, items, nuevoWatermark);
  }

  async getEstadisticasByLoteIdAsync<T>(loteId: string): Promise<T[]> {
    const db = await this.open();
    const index = db
      .transaction(STORE_ESTADISTICAS, "readonly")
      .objectStore(STORE_ESTADISTICAS)
      .index("fincaLoteId");
    return reqToPromise<T[]>(index.getAll(loteId));
  }
}

/** Singleton de la base de datos satelitales por gRPC-Web (con delta por
 *  finca/lote). */
export const grpcSateliteDatabase = new GrpcSateliteDatabase();
